import { useState, useCallback, useRef } from 'react'
import type { ReportData, BrandingConfig, PDFGenerationState } from '@/types'

/**
 * Hook for generating PDF reports from the Value of Advice calculator
 * Uses html2canvas and jsPDF for client-side PDF generation
 */
export function usePDFGenerator() {
  const [state, setState] = useState<PDFGenerationState>({
    isGenerating: false,
    progress: 0,
    currentPage: 0,
    totalPages: 5,
    error: null,
  })

  const containerRef = useRef<HTMLDivElement | null>(null)

  const generatePDF = useCallback(
    async (data: ReportData, branding: BrandingConfig, container: HTMLDivElement | null) => {
      if (!container) {
        setState((prev) => ({ ...prev, error: 'PDF container not found' }))
        return
      }

      setState({
        isGenerating: true,
        progress: 0,
        currentPage: 0,
        totalPages: 5,
        error: null,
      })

      try {
        // Dynamically import heavy libraries
        const [html2canvasModule, jsPDFModule] = await Promise.all([
          import('html2canvas'),
          import('jspdf'),
        ])

        const html2canvas = html2canvasModule.default
        const { jsPDF } = jsPDFModule

        // Wait for any fonts/images to load
        await new Promise((resolve) => setTimeout(resolve, 500))

        // Get all A4 pages
        const pages = container.querySelectorAll('.a4-page')
        if (pages.length === 0) {
          throw new Error('No pages found in PDF container')
        }

        // Create PDF document
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4',
        })

        const pageWidth = 210
        const pageHeight = 297

        for (let i = 0; i < pages.length; i++) {
          const page = pages[i] as HTMLElement

          setState((prev) => ({
            ...prev,
            currentPage: i + 1,
            progress: Math.round(((i + 1) / pages.length) * 100),
          }))

          // Capture page as canvas
          const canvas = await html2canvas(page, {
            scale: 2, // Higher quality
            useCORS: true,
            allowTaint: true,
            backgroundColor: branding.theme === 'light' ? '#ffffff' : '#1a1f2e',
            logging: false,
          })

          // Add page to PDF
          const imgData = canvas.toDataURL('image/png')

          if (i > 0) {
            pdf.addPage()
          }

          pdf.addImage(imgData, 'PNG', 0, 0, pageWidth, pageHeight)
        }

        // Generate filename
        const dateStr = new Date().toISOString().split('T')[0]
        const clientName = data.clientName?.replace(/[^a-zA-Z0-9]/g, '-') || 'client'
        const filename = `value-of-advice-${clientName}-${dateStr}.pdf`

        // Download PDF
        pdf.save(filename)

        setState((prev) => ({
          ...prev,
          isGenerating: false,
          progress: 100,
        }))
      } catch (error) {
        console.error('PDF generation error:', error)
        setState((prev) => ({
          ...prev,
          isGenerating: false,
          error: error instanceof Error ? error.message : 'Failed to generate PDF',
        }))
      }
    },
    []
  )

  const reset = useCallback(() => {
    setState({
      isGenerating: false,
      progress: 0,
      currentPage: 0,
      totalPages: 5,
      error: null,
    })
  }, [])

  return {
    ...state,
    generatePDF,
    reset,
    containerRef,
  }
}

export default usePDFGenerator
